import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchCurrencies,
  fetchPaymentMethods,
  fetchPlanPriceConfigurations,
  createPlanPriceConfiguration,
  updatePlanPriceConfiguration,
  deletePlanPriceConfiguration,
} from "@/data/mockData";
import { Currency, PaymentMethod, PlanPriceConfiguration } from "@/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlusCircle, Save, X, Info, Edit, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { convertPrice, formatPriceWithCurrency } from "@/api/payments";

interface PricingConfiguratorProps {
  planId: string | number;
  basePrice: number;
  baseCurrency?: string;
  onSave?: () => void;
}

export default function PricingConfigurator({
  planId,
  basePrice,
  baseCurrency = "INR",
  onSave,
}: PricingConfiguratorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State for new configuration
  const [selectedCurrency, setSelectedCurrency] =
    useState<string>(baseCurrency);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    number | null
  >(null);
  const [newPrice, setNewPrice] = useState<string>("");
  const [editingConfig, setEditingConfig] =
    useState<PlanPriceConfiguration | null>(null);
  // Add state for active tab
  const [activeTab, setActiveTab] = useState<string>("configurations");
  // Add state to track if current selection is a duplicate
  const [isDuplicateConfig, setIsDuplicateConfig] = useState<boolean>(false);

  // Fetch data
  const { data: currencies = [] } = useQuery({
    queryKey: ["currencies"],
    queryFn: fetchCurrencies,
  });

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: fetchPaymentMethods,
  });

  const {
    data: priceConfigurations = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["planPriceConfigurations", planId],
    queryFn: () => fetchPlanPriceConfigurations(planId),
  });

  // Filter payment methods by selected currency
  const availablePaymentMethods = paymentMethods.filter((method) =>
    method.supportedCurrencies.includes(selectedCurrency)
  );

  // Group configurations by currency
  const configsByCurrency: Record<string, PlanPriceConfiguration[]> = {};
  priceConfigurations.forEach((config) => {
    if (!configsByCurrency[config.currencyCode]) {
      configsByCurrency[config.currencyCode] = [];
    }
    configsByCurrency[config.currencyCode].push(config);
  });

  // Suggest a price when payment method and currency changes
  useEffect(() => {
    if (selectedCurrency && selectedPaymentMethod && basePrice) {
      const currency = currencies.find((c) => c.code === selectedCurrency);
      const paymentMethod = paymentMethods.find(
        (m) => m.id === selectedPaymentMethod
      );
      const baseCurrencyObj = currencies.find((c) => c.code === baseCurrency);

      if (currency && paymentMethod && baseCurrencyObj) {
        // Start with a base price in the selected currency
        let suggestedPrice: number;

        // First convert base currency price to the selected currency (rough estimate only)
        if (currency.code === baseCurrency) {
          suggestedPrice = basePrice;
        } else {
          // Convert from base currency to selected currency
          // Formula: base price / base currency exchange rate * selected currency exchange rate
          suggestedPrice =
            (basePrice / baseCurrencyObj.exchangeRate) * currency.exchangeRate;
        }

        // Then adjust for payment method fees
        if (paymentMethod.feeType === "percentage") {
          // If platform takes 30%, we need to increase our price to maintain the same revenue
          // Formula: adjusted price = original price / (1 - fee percentage)
          const feeMultiplier = 1 - paymentMethod.platformFee / 100;
          suggestedPrice = suggestedPrice / feeMultiplier;
        } else {
          // For fixed fees, simply add the fee converted to the target currency
          const feeInBaseCurrency = paymentMethod.platformFee;
          const feeInSelectedCurrency =
            (feeInBaseCurrency /
              currencies.find((c) => c.code === "USD")!.exchangeRate) *
            currency.exchangeRate;
          suggestedPrice = suggestedPrice + feeInSelectedCurrency;
        }

        // Round appropriately based on currency
        if (["USD", "EUR", "GBP"].includes(currency.code)) {
          // For USD/EUR/GBP, keep prices at .99 endings for marketing purposes
          suggestedPrice = Math.ceil(suggestedPrice) - 0.01;
        } else if (["JPY", "INR", "MMK"].includes(currency.code)) {
          // For INR/JPY, round to 9 at the end (e.g. 199, 299, etc.)
          suggestedPrice = Math.ceil(suggestedPrice / 10) * 10 - 1;
        }

        setNewPrice(suggestedPrice.toString());
      }
    }
  }, [
    selectedCurrency,
    selectedPaymentMethod,
    basePrice,
    baseCurrency,
    currencies,
    paymentMethods,
  ]);

  // Update first available payment method when currency changes
  useEffect(() => {
    if (availablePaymentMethods.length > 0 && !selectedPaymentMethod) {
      setSelectedPaymentMethod(availablePaymentMethods[0].id as number);
    }
  }, [availablePaymentMethods, selectedPaymentMethod]);

  // Check for duplicate configurations when currency or payment method changes
  useEffect(() => {
    if (selectedCurrency && selectedPaymentMethod && !editingConfig) {
      const existingConfig = priceConfigurations.find(
        (config) =>
          config.currencyCode === selectedCurrency &&
          config.paymentMethodId === selectedPaymentMethod
      );

      setIsDuplicateConfig(!!existingConfig);
    } else {
      setIsDuplicateConfig(false);
    }
  }, [
    selectedCurrency,
    selectedPaymentMethod,
    priceConfigurations,
    editingConfig,
  ]);

  // Add effect to reset state when switching tabs
  useEffect(() => {
    if (activeTab === "configurations") {
      setEditingConfig(null);
      setIsDuplicateConfig(false);
    }
  }, [activeTab]);

  // Create configuration mutation
  const createConfigMutation = useMutation({
    mutationFn: createPlanPriceConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["planPriceConfigurations", planId],
      });
      resetForm();
      setActiveTab("configurations");
      toast({
        title: "Price configuration added",
        description: "The price configuration has been added successfully.",
      });
      if (onSave) onSave();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update configuration mutation
  const updateConfigMutation = useMutation({
    mutationFn: (params: {
      id: number;
      data: Partial<Omit<PlanPriceConfiguration, "id">>;
    }) => updatePlanPriceConfiguration(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["planPriceConfigurations", planId],
      });
      resetForm();
      setActiveTab("configurations");
      toast({
        title: "Price configuration updated",
        description: "The price configuration has been updated successfully.",
      });
      if (onSave) onSave();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete configuration mutation
  const deleteConfigMutation = useMutation({
    mutationFn: deletePlanPriceConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["planPriceConfigurations", planId],
      });
      toast({
        title: "Price configuration deleted",
        description: "The price configuration has been deleted successfully.",
      });
      if (onSave) onSave();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Reset form fields
  const resetForm = () => {
    setSelectedCurrency(baseCurrency);
    setSelectedPaymentMethod(null);
    setNewPrice("");
    setEditingConfig(null);
    setIsDuplicateConfig(false);
  };

  // Handle adding a new configuration
  const handleAddConfiguration = async () => {
    if (
      !selectedCurrency ||
      selectedPaymentMethod === null ||
      !newPrice ||
      isDuplicateConfig
    ) {
      return;
    }

    const parsedPrice = parseFloat(newPrice);
    if (isNaN(parsedPrice) || parsedPrice <= 0) {
      toast({
        title: "Invalid price",
        description: "Please enter a valid positive price.",
        variant: "destructive",
      });
      return;
    }

    try {
      interface CreateConfigData {
        planId: number;
        paymentMethodId: number;
        currencyCode: string;
        price: number;
        isActive: boolean;
      }

      const configData: CreateConfigData = {
        planId: Number(planId),
        paymentMethodId: selectedPaymentMethod,
        currencyCode: selectedCurrency,
        price: parsedPrice,
        isActive: true,
      };

      await createConfigMutation.mutateAsync(configData);
    } catch (error) {
      console.error("Error adding price configuration:", error);
    }
  };

  // Handle updating an existing configuration
  const handleUpdateConfiguration = async () => {
    if (!editingConfig || !newPrice) {
      return;
    }

    const parsedPrice = parseFloat(newPrice);
    if (isNaN(parsedPrice) || parsedPrice <= 0) {
      toast({
        title: "Invalid price",
        description: "Please enter a valid positive price.",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateConfigMutation.mutateAsync({
        id: editingConfig.id,
        data: {
          price: parsedPrice,
          isActive: editingConfig.isActive,
        },
      });
    } catch (error) {
      console.error("Error updating price configuration:", error);
    }
  };

  // Start editing a configuration
  const startEditing = (config: PlanPriceConfiguration) => {
    setEditingConfig(config);
    setNewPrice(config.price.toString());
    setSelectedCurrency(config.currencyCode);
    setSelectedPaymentMethod(config.paymentMethodId as number);

    // Switch to the edit tab
    setActiveTab("add");
  };

  // Toggle configuration active state
  const toggleConfigActive = async (config: PlanPriceConfiguration) => {
    try {
      await updateConfigMutation.mutateAsync({
        id: config.id,
        data: {
          isActive: !config.isActive,
        },
      });
    } catch (error) {
      console.error("Error toggling configuration status:", error);
    }
  };

  // Format price with currency
  const formatPrice = (price: number, currencyCode: string) => {
    const currency = currencies.find((c) => c.code === currencyCode);
    if (!currency) return `${price}`;
    return formatPriceWithCurrency(price, currency);
  };

  // Get payment method name by ID
  const getPaymentMethodName = (id: string | number) => {
    const method = paymentMethods.find((m) => m.id === id);
    return method ? method.name : id;
  };

  // Get payment method fee by ID
  const getPaymentMethodFee = (id: string | number) => {
    const method = paymentMethods.find((m) => m.id === id);
    if (!method) return "";
    return method.feeType === "percentage"
      ? `${method.platformFee}%`
      : formatPrice(method.platformFee, "USD");
  };

  // Calculate actual revenue from a price configuration
  const calculateNetRevenue = (config: PlanPriceConfiguration) => {
    const paymentMethod = paymentMethods.find(
      (m) => m.id === config.paymentMethodId
    );
    const currency = currencies.find((c) => c.code === config.currencyCode);
    const baseCurrencyObj = currencies.find((c) => c.code === baseCurrency);

    if (!paymentMethod || !currency || !baseCurrencyObj) return null;

    let netAmount = config.price;

    // Deduct platform fee
    if (paymentMethod.feeType === "percentage") {
      netAmount -= config.price * (paymentMethod.platformFee / 100);
    } else {
      netAmount -= paymentMethod.platformFee;
    }

    // Convert to base currency for comparison
    if (currency.code !== baseCurrency) {
      netAmount =
        (netAmount / currency.exchangeRate) * baseCurrencyObj.exchangeRate;
    }

    return netAmount;
  };

  // Prepare exchange rate tooltip content
  const getExchangeRateTooltipContent = () => {
    if (currencies.length < 2) return "No exchange rate data available";

    const baseCurrencyObj = currencies.find((c) => c.code === baseCurrency);
    if (!baseCurrencyObj) return "Base currency not found";

    return (
      <div className="space-y-2">
        <p className="font-semibold">Exchange Rates:</p>
        <div className="space-y-1">
          {currencies
            .filter((c) => c.code !== baseCurrency)
            .map((currency) => {
              // Special case for MMK - show 1 INR = X MMK
              if (currency.code === "MMK" && baseCurrency !== "MMK") {
                return (
                  <div key={currency.code} className="flex justify-between">
                    <span>1 {baseCurrency}</span>
                    <span>=</span>
                    <span>
                      {(
                        currency.exchangeRate / baseCurrencyObj.exchangeRate
                      ).toFixed(2)}{" "}
                      {currency.code}
                    </span>
                  </div>
                );
              }

              // Original format for other currencies
              return (
                <div key={currency.code} className="flex justify-between">
                  <span>1 {currency.code}</span>
                  <span>=</span>
                  <span>
                    {(
                      baseCurrencyObj.exchangeRate / currency.exchangeRate
                    ).toFixed(2)}{" "}
                    {baseCurrency}
                  </span>
                </div>
              );
            })}
        </div>
        <p className="text-xs text-muted-foreground pt-1">
          Last updated: {new Date().toLocaleDateString()}
        </p>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Price Configurations
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Configure prices for different currencies and payment methods.
                Each combination needs its own price to account for different
                transaction fees and market conditions.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
        <CardDescription>
          Set up pricing for different currencies and payment methods
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="configurations">
              Current Configurations
            </TabsTrigger>
            <TabsTrigger value="add">
              {editingConfig ? "Edit Configuration" : "Add New Configuration"}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="configurations">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
              </div>
            ) : Object.keys(configsByCurrency).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No price configurations yet. Add your first one!
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(configsByCurrency).map(
                  ([currencyCode, configs]) => (
                    <div key={currencyCode}>
                      <h3 className="text-lg font-medium mb-2 flex items-center gap-2">
                        {currencyCode}
                        <Badge variant="outline">
                          {
                            currencies.find((c) => c.code === currencyCode)
                              ?.name
                          }
                        </Badge>
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Payment Method</TableHead>
                            <TableHead>Platform Fee</TableHead>
                            <TableHead>Price</TableHead>
                            <TableHead>
                              <div className="flex items-center gap-1">
                                Est. Revenue ({baseCurrency})
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent className="w-60">
                                      {getExchangeRateTooltipContent()}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {configs.map((config) => {
                            const netRevenue = calculateNetRevenue(config);
                            const isRevenueDifferent =
                              Math.abs((netRevenue || 0) - basePrice) >
                              (baseCurrency === "INR" ? 50 : 0.5);

                            return (
                              <TableRow
                                key={config.id}
                                className={!config.isActive ? "opacity-50" : ""}
                              >
                                <TableCell>
                                  {getPaymentMethodName(config.paymentMethodId)}
                                </TableCell>
                                <TableCell>
                                  {getPaymentMethodFee(config.paymentMethodId)}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {formatPrice(
                                    config.price,
                                    config.currencyCode
                                  )}
                                </TableCell>
                                <TableCell>
                                  {netRevenue !== null && (
                                    <div className="flex items-center gap-1">
                                      {currencies.find(
                                        (c) => c.code === baseCurrency
                                      )?.symbol || ""}
                                      {baseCurrency === "INR"
                                        ? Math.round(netRevenue).toString()
                                        : netRevenue.toFixed(2)}
                                      {isRevenueDifferent && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Info className="h-4 w-4 text-amber-500 cursor-help" />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              {netRevenue > basePrice
                                                ? `Revenue is higher than base price (${formatPrice(
                                                    basePrice,
                                                    baseCurrency
                                                  )})`
                                                : `Revenue is lower than base price (${formatPrice(
                                                    basePrice,
                                                    baseCurrency
                                                  )})`}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={
                                      config.isActive ? "default" : "outline"
                                    }
                                  >
                                    {config.isActive ? "Active" : "Inactive"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => toggleConfigActive(config)}
                                    >
                                      {config.isActive ? (
                                        <X className="h-4 w-4" />
                                      ) : (
                                        <Check className="h-4 w-4" />
                                      )}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => startEditing(config)}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="add">
            <div className="space-y-4">
              {editingConfig && (
                <div className="rounded-md bg-muted p-3 text-sm">
                  Note: Currency and payment method cannot be changed when
                  editing a price configuration. To use a different currency or
                  payment method, create a new configuration instead.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    value={selectedCurrency}
                    onValueChange={setSelectedCurrency}
                    disabled={editingConfig !== null}
                  >
                    <SelectTrigger
                      id="currency"
                      className={isDuplicateConfig ? "border-destructive" : ""}
                    >
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          <div className="flex items-center gap-2">
                            <span>{currency.symbol}</span>
                            <span>{currency.name}</span>
                            <span className="text-muted-foreground">
                              ({currency.code})
                            </span>
                            {currency.code === baseCurrency && (
                              <Badge variant="outline" className="ml-1">
                                Base
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentMethod">Payment Method</Label>
                  <Select
                    value={selectedPaymentMethod?.toString() || ""}
                    onValueChange={(value) =>
                      setSelectedPaymentMethod(Number(value))
                    }
                    disabled={
                      availablePaymentMethods.length === 0 ||
                      editingConfig !== null
                    }
                  >
                    <SelectTrigger
                      id="paymentMethod"
                      className={isDuplicateConfig ? "border-destructive" : ""}
                    >
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePaymentMethods.map((method) => (
                        <SelectItem
                          key={method.id}
                          value={method.id.toString()}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span>{method.name}</span>
                            <Badge variant="outline" className="ml-2">
                              {method.feeType === "percentage"
                                ? `${method.platformFee}%`
                                : formatPrice(method.platformFee, "USD")}{" "}
                              fee
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {availablePaymentMethods.length === 0 && selectedCurrency && (
                    <p className="text-sm text-destructive pt-1">
                      No payment methods available for {selectedCurrency}
                    </p>
                  )}
                  {isDuplicateConfig && (
                    <p className="text-sm text-destructive pt-1">
                      A configuration for this currency and payment method
                      already exists
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Price</Label>
                  <div className="relative flex items-center">
                    <div className="absolute left-3">
                      {currencies.find((c) => c.code === selectedCurrency)
                        ?.symbol || "₹"}
                    </div>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      step={
                        ["INR", "JPY"].includes(selectedCurrency) ? "1" : "0.01"
                      }
                      value={newPrice}
                      onChange={(e) => {
                        // Round to whole numbers for currencies like INR and JPY
                        if (["INR", "JPY"].includes(selectedCurrency)) {
                          setNewPrice(
                            Math.round(
                              parseFloat(e.target.value) || 0
                            ).toString()
                          );
                        } else {
                          setNewPrice(e.target.value);
                        }
                      }}
                      className="pl-7"
                      placeholder="Enter price"
                    />
                  </div>
                  {selectedPaymentMethod && newPrice && (
                    <div className="text-xs mt-1">
                      {(() => {
                        const method = paymentMethods.find(
                          (m) => m.id === selectedPaymentMethod
                        );
                        const currency = currencies.find(
                          (c) => c.code === selectedCurrency
                        );
                        const baseCurrencyObj = currencies.find(
                          (c) => c.code === baseCurrency
                        );

                        if (
                          !method ||
                          !currency ||
                          !newPrice ||
                          !baseCurrencyObj
                        )
                          return null;

                        const price = parseFloat(newPrice);
                        let netAmount = price;

                        if (method.feeType === "percentage") {
                          const feeAmount = price * (method.platformFee / 100);
                          netAmount = price - feeAmount;

                          // Convert to base currency if needed
                          let baseCurrencyAmount = netAmount;
                          if (currency.code !== baseCurrency) {
                            baseCurrencyAmount =
                              (netAmount / currency.exchangeRate) *
                              baseCurrencyObj.exchangeRate;
                          }

                          const formattedBaseAmount =
                            baseCurrency === "INR"
                              ? Math.round(baseCurrencyAmount)
                              : baseCurrencyAmount.toFixed(2);

                          return (
                            <span
                              className={
                                baseCurrencyAmount < basePrice * 0.95
                                  ? "text-destructive"
                                  : "text-muted-foreground"
                              }
                            >
                              After {method.platformFee}% fee:{" "}
                              {formatPrice(price - feeAmount, selectedCurrency)}
                              {currency.code !== baseCurrency
                                ? ` (${baseCurrencyObj.symbol}${formattedBaseAmount} ${baseCurrency})`
                                : ""}
                            </span>
                          );
                        } else {
                          const feeAmount = method.platformFee;
                          // Convert fee to selected currency
                          const feeInSelectedCurrency =
                            (feeAmount /
                              currencies.find((c) => c.code === "USD")!
                                .exchangeRate) *
                            currency.exchangeRate;
                          netAmount = price - feeInSelectedCurrency;

                          // Convert to base currency if needed
                          let baseCurrencyAmount = netAmount;
                          if (currency.code !== baseCurrency) {
                            baseCurrencyAmount =
                              (netAmount / currency.exchangeRate) *
                              baseCurrencyObj.exchangeRate;
                          }

                          const formattedBaseAmount =
                            baseCurrency === "INR"
                              ? Math.round(baseCurrencyAmount)
                              : baseCurrencyAmount.toFixed(2);

                          return (
                            <span
                              className={
                                baseCurrencyAmount < basePrice * 0.95
                                  ? "text-destructive"
                                  : "text-muted-foreground"
                              }
                            >
                              After{" "}
                              {formatPrice(
                                feeInSelectedCurrency,
                                selectedCurrency
                              )}{" "}
                              fee: {formatPrice(netAmount, selectedCurrency)}
                              {currency.code !== baseCurrency
                                ? ` (${baseCurrencyObj.symbol}${formattedBaseAmount} ${baseCurrency})`
                                : ""}
                            </span>
                          );
                        }
                      })()}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                {editingConfig ? (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingConfig(null);
                        setActiveTab("configurations");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleUpdateConfiguration}>
                      <Save className="h-4 w-4 mr-2" /> Update Configuration
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleAddConfiguration}
                    disabled={
                      availablePaymentMethods.length === 0 || isDuplicateConfig
                    }
                  >
                    <PlusCircle className="h-4 w-4 mr-2" /> Add Price
                    Configuration
                  </Button>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
